import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Col, Modal, Row, Table } from "react-bootstrap";

import { rockGemsApi } from "../../services/rockGemsApi";
import { normalizePaginate } from "../../utils/normalizePaginate";
import ErrorAlert from "../common/ErrorAlert";
import LoadingBlock from "../common/LoadingBlock";
import PaginationBar from "../common/PaginationBar";
import GemDetailModal from "./GemDetailModal";

function getCategoryUid(c) {
  return c?.uid || c?.cms_uid || c?.cmsUid || c?.category_uid || c?.slug || c?.key;
}

function getCategoryTitle(c) {
  return c?.title || c?.name || c?.label || getCategoryUid(c) || "Category";
}

/** @returns {'birthstones' | 'zodiac' | null} */
function resolveGroupedGemsMode(category) {
  if (!category || typeof category !== "object") return null;
  const tokens = [
    getCategoryUid(category),
    category.label,
    category.slug,
    category.key,
    category.title,
    category.name
  ]
    .filter(Boolean)
    .map((s) => String(s).toLowerCase());

  for (const t of tokens) {
    if (t.includes("birthstone")) return "birthstones";
  }
  for (const t of tokens) {
    if (t.includes("zodiac")) return "zodiac";
  }
  return null;
}

function getGemUid(g) {
  return g?.uid || g?.id || g?.gem_uid;
}

function getGemTitle(g) {
  return (
    g?.preferred_name ||
    g?.latin_name ||
    g?.common_name ||
    g?.title ||
    g?.name ||
    g?.label ||
    getGemUid(g) ||
    "Gem"
  );
}

function sortGroupsWithDefaultFirst(groups) {
  if (!Array.isArray(groups)) return [];
  const def = groups.filter((g) => g?.uid === "default");
  const rest = groups.filter((g) => g?.uid !== "default");
  return [...def, ...rest];
}

function GroupIntro({ group }) {
  const intro = group?.items?.[0];
  if (!intro?.summary && !intro?.main_image_url) return null;
  return (
    <div className="mb-4 p-3 rounded border bg-body-secondary bg-opacity-25">
      {intro.main_image_url ? (
        <img
          src={intro.main_image_url}
          alt=""
          className="rounded mb-2 d-block"
          style={{ maxHeight: 140, objectFit: "cover", width: "100%" }}
        />
      ) : null}
      {intro.summary ? <div className="small text-muted">{intro.summary}</div> : null}
    </div>
  );
}

function GemGroupTable({ group, onGemDetail }) {
  const items = group?.items || [];
  const title = group?.title || group?.label || "—";

  if (!items.length) return null;

  const showAsIntroOnly =
    group.uid === "default" &&
    items.length === 1 &&
    !items[0]?.latin_name &&
    !items[0]?.common_name &&
    (items[0]?.summary || items[0]?.main_image_url);

  if (showAsIntroOnly) {
    return <GroupIntro group={group} />;
  }

  return (
    <div className="mb-4">
      <div className="d-flex align-items-center gap-2 flex-wrap border-bottom pb-2 mb-2">
        {group.image_url ? (
          <img
            src={group.image_url}
            alt=""
            className="rounded"
            style={{ width: 48, height: 48, objectFit: "cover" }}
          />
        ) : null}
        <h6 className="mb-0">{title}</h6>
        {group.label ? (
          <Badge bg="secondary" className="badge-pill">
            {group.label}
          </Badge>
        ) : null}
      </div>
      <div className="table-responsive">
        <Table hover className="table-vcenter table-songsnap mb-0">
          <thead>
            <tr>
              <th style={{ width: 220 }}>UID</th>
              <th>Name</th>
              <th style={{ width: 140 }} />
            </tr>
          </thead>
          <tbody>
            {items.map((g) => {
              const uid = getGemUid(g);
              return (
                <tr key={uid || JSON.stringify(g)}>
                  <td className="text-muted small">{uid}</td>
                  <td className="fw-bold">{getGemTitle(g)}</td>
                  <td className="text-end">
                    <Button size="sm" variant="primary" onClick={() => onGemDetail(uid)}>
                      Detail
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    </div>
  );
}

export default function CategoryGemsModal({ show, onHide, category }) {
  const category_uid = category ? getCategoryUid(category) : null;
  const categoryTitle = category ? getCategoryTitle(category) : null;
  const groupedMode = useMemo(() => resolveGroupedGemsMode(category), [category]);

  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gems, setGems] = useState([]);
  const [meta, setMeta] = useState({});
  const [groups, setGroups] = useState([]);

  const [gemDetailUid, setGemDetailUid] = useState(null);

  useEffect(() => {
    if (!show) return;
    setPage(1);
    setGemDetailUid(null);
  }, [show, category_uid, groupedMode]);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!show || !category_uid) return;
      setLoading(true);
      setError(null);
      try {
        if (groupedMode === "birthstones") {
          const data = await rockGemsApi.getGemsBirthstones();
          if (!cancelled) {
            setGroups(Array.isArray(data) ? data : []);
            setGems([]);
            setMeta({});
          }
        } else if (groupedMode === "zodiac") {
          const data = await rockGemsApi.getGemsZodiac();
          if (!cancelled) {
            setGroups(Array.isArray(data) ? data : []);
            setGems([]);
            setMeta({});
          }
        } else {
          const payload = await rockGemsApi.getGems({
            category_uid,
            page,
            items_per_page: 20
          });
          const normalized = normalizePaginate(payload);
          if (!cancelled) {
            setGems(normalized.items);
            setMeta(normalized.meta);
            setGroups([]);
          }
        }
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [show, category_uid, page, groupedMode]);

  const sortedGroups = useMemo(() => sortGroupsWithDefaultFirst(groups), [groups]);

  const canNext = useMemo(() => {
    if (groupedMode) return false;
    if (typeof meta.total === "number" && typeof meta.items_per_page === "number") {
      return page * meta.items_per_page < meta.total;
    }
    return (gems || []).length >= 20;
  }, [meta, page, gems, groupedMode]);

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2">
            <span>{categoryTitle || "Category"}</span>
            {category_uid ? (
              <Badge bg="secondary" className="badge-pill">
                {category_uid}
              </Badge>
            ) : null}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ErrorAlert error={error} title="Failed to load gems" />
          {loading ? <LoadingBlock label="Loading gems..." /> : null}

          {!loading && groupedMode ? (
            <>
              {sortedGroups.length ? (
                sortedGroups.map((grp) => (
                  <GemGroupTable
                    key={grp.uid || grp.label || JSON.stringify(grp)}
                    group={grp}
                    onGemDetail={setGemDetailUid}
                  />
                ))
              ) : (
                <div className="text-center text-muted py-4">No gems found.</div>
              )}
            </>
          ) : null}

          {!loading && !groupedMode ? (
            <>
              <div className="table-responsive">
                <Table hover className="table-vcenter table-songsnap">
                  <thead>
                    <tr>
                      <th style={{ width: 220 }}>UID</th>
                      <th>Name</th>
                      <th style={{ width: 140 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {(gems || []).map((g) => {
                      const uid = getGemUid(g);
                      return (
                        <tr key={uid || JSON.stringify(g)}>
                          <td className="text-muted small">{uid}</td>
                          <td className="fw-bold">{getGemTitle(g)}</td>
                          <td className="text-end">
                            <Button size="sm" variant="primary" onClick={() => setGemDetailUid(uid)}>
                              Detail
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                    {!gems?.length ? (
                      <tr>
                        <td colSpan={3} className="text-center text-muted py-4">
                          No gems found.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </Table>
              </div>

              <Row className="align-items-center">
                <Col>
                  <div className="text-muted small">
                    Page <b>{page}</b>
                    {typeof meta.total === "number" ? (
                      <>
                        {" "}
                        · Total <b>{meta.total}</b>
                      </>
                    ) : null}
                  </div>
                </Col>
                <Col xs="auto">
                  <PaginationBar
                    page={page}
                    onPageChange={(p) => setPage(Math.max(1, p))}
                    canPrev={page > 1}
                    canNext={canNext}
                  />
                </Col>
              </Row>
            </>
          ) : null}
        </Modal.Body>
      </Modal>

      <GemDetailModal
        show={!!gemDetailUid}
        onHide={() => setGemDetailUid(null)}
        uid={gemDetailUid}
        category_uid={category_uid}
      />
    </>
  );
}
